/**
 * equityClient.ts —— 赢率计算通道（Worker 封装 + 无 Worker 环境的同步兜底）
 *
 * 通道语义：同一时刻只保留 1 个 in-flight 请求（requestId 防串台）。
 * 新请求进来会先以 `superseded` 取消旧请求，避免上一街的结果覆盖新街（AC4.7）。
 */

import type { Card } from '../core/cards';
import type { CancelReason, EquityProgress, EquityResult } from '../core/equity';
import type { EquityRequest, EquityResponse } from './equityTypes';

export interface CalcHandlers {
  onProgress?: (p: EquityProgress) => void;
  onResult: (r: EquityResult) => void;
  /** kind: unavailable = 离线表不可用；cancelled = 已取消；failed = 其他异常。 */
  onError: (kind: 'unavailable' | 'cancelled' | 'failed', message: string, detail?: string) => void;
}

export interface EquityChannel {
  /** 发起计算。返回本次请求的 requestId。 */
  request(hole: readonly Card[], board: readonly Card[], handlers: CalcHandlers, slowBurnMs?: number): number;
  /** 取消当前 in-flight 请求（幂等）。 */
  cancel(reason: CancelReason): void;
  /** 是否有计算在进行中。 */
  busy(): boolean;
  dispose(): void;
}

/* ------------------------------------------------------------------ */
/* Worker 通道                                                         */
/* ------------------------------------------------------------------ */

export function createWorkerChannel(): EquityChannel {
  let worker: Worker | null = null;
  let seq = 0;
  let inflight: { id: number; handlers: CalcHandlers } | null = null;

  const spawn = (): Worker => {
    const w = new Worker(new URL('../workers/equity.worker.ts', import.meta.url), {
      type: 'module',
      name: 'riverside-equity',
    });
    /*
     * 出错即丢弃这个实例（QA-BUG-022 续）。
     *
     * 真机现场：worker 脚本加载失败会走 onerror，改前只清 inflight、保留 `worker` 变量，
     * 于是下一次 request 命中 `if (!worker)` 为假 → 往一个**脚本已加载失败的死实例**
     * postMessage，消息被静默丢弃，回调永远不来。结果是「重试」按钮点了也只能再等满一次
     * 硬超时，即使网络早已恢复（verify-worker-error.mjs 实测第 3 次仍 error@4001ms）。
     *
     * worker 是无状态的（每次计算的输入都随消息带全），丢弃重开没有任何代价。
     */
    const discard = (): void => {
      if (worker === w) worker = null;
      try {
        w.terminate();
      } catch {
        /* 已终止 / 环境不支持时忽略 */
      }
    };
    w.onmessage = (ev: MessageEvent<EquityResponse>) => {
      const msg = ev.data;
      if (!inflight || msg.requestId !== inflight.id) return; // 串台防护：丢弃过期结果
      const { handlers } = inflight;
      if (msg.type === 'progress') {
        handlers.onProgress?.({
          street: msg.street,
          boardsEnumerated: msg.boardsEnumerated,
          boardsTotal: msg.boardsTotal,
          ratio: msg.ratio,
          elapsedMs: msg.elapsedMs,
        });
        return;
      }
      inflight = null;
      if (msg.type === 'result') handlers.onResult(msg.result);
      else handlers.onError(msg.kind, msg.message, msg.detail);
    };
    w.onerror = (e) => {
      const cur = inflight;
      inflight = null;
      discard();
      cur?.handlers.onError('failed', `赢率 Worker 异常：${e.message}`);
    };
    /*
     * messageerror = 结构化克隆失败（消息本身发不过来 / 收不回来）。
     * 与 onerror 不同，它不会走上面的分支，改前这条路径会让请求永久悬挂。
     * 注：module worker 的「资产加载失败」在部分浏览器两个回调都不触发，
     * 兜底靠 EquityService 的硬超时看门狗（HARD_TIMEOUT_MS），不在通道层重复实现。
     */
    w.onmessageerror = () => {
      const cur = inflight;
      inflight = null;
      discard();
      cur?.handlers.onError('failed', '赢率 Worker 消息解析失败');
    };
    return w;
  };

  const hardKill = (): void => {
    if (worker) worker.terminate();
    worker = null;
  };

  return {
    request(hole, board, handlers, slowBurnMs) {
      if (inflight) this.cancel('superseded');
      seq += 1;
      inflight = { id: seq, handlers };
      const req: EquityRequest = { type: 'calc', requestId: seq, hole: [...hole], board: [...board], slowBurnMs };
      try {
        if (!worker) worker = spawn();
        worker.postMessage(req);
      } catch (e) {
        // `new Worker()` 在资产缺失 / CSP 拦截时会同步抛错，改前这里会把异常冒泡到调用方，
        // 面板拿不到任何回调 → 永久 loading。统一转成 failed，让状态机落到 error 态。
        worker = null;
        const cur = inflight;
        inflight = null;
        cur?.handlers.onError('failed', `赢率 Worker 启动失败：${e instanceof Error ? e.message : String(e)}`);
      }
      return seq;
    },
    cancel(reason) {
      const cur = inflight;
      if (!cur) return;
      inflight = null;
      // 软取消 + 硬终止：保证 ≤100ms 真正停止（Worker 同步枚举期间收不到消息）
      worker?.postMessage({ type: 'cancel', requestId: cur.id, reason } satisfies EquityRequest);
      hardKill();
      cur.handlers.onError('cancelled', `赢率计算已取消：${reason}`, reason);
    },
    busy: () => inflight !== null,
    dispose() {
      inflight = null;
      hardKill();
    },
  };
}

/* ------------------------------------------------------------------ */
/* 同步兜底通道（Node 单测 / 不支持 Worker 的环境）                     */
/* ------------------------------------------------------------------ */

export function createSyncChannel(
  calc: (
    hole: readonly Card[],
    board: readonly Card[],
    opts: { onProgress?: (p: EquityProgress) => void; cancelToken?: { cancelled: boolean; reason: CancelReason | null; cancel(r: CancelReason): void } },
  ) => EquityResult,
): EquityChannel {
  let seq = 0;
  let inflight: { id: number; handlers: CalcHandlers; timer: ReturnType<typeof setTimeout> } | null = null;

  return {
    request(hole, board, handlers) {
      if (inflight) this.cancel('superseded');
      seq += 1;
      const id = seq;
      const timer = setTimeout(() => {
        if (!inflight || inflight.id !== id) return;
        inflight = null;
        try {
          const r = calc(hole, board, { onProgress: handlers.onProgress });
          handlers.onResult(r);
        } catch (e) {
          const name = e instanceof Error ? e.name : '';
          const kind = name === 'PreflopTableUnavailableError' ? 'unavailable' : name === 'EquityCancelledError' ? 'cancelled' : 'failed';
          handlers.onError(kind, e instanceof Error ? e.message : String(e));
        }
      }, 0);
      inflight = { id, handlers, timer };
      return id;
    },
    cancel(reason) {
      const cur = inflight;
      if (!cur) return;
      inflight = null;
      clearTimeout(cur.timer);
      cur.handlers.onError('cancelled', `赢率计算已取消：${reason}`, reason);
    },
    busy: () => inflight !== null,
    dispose() {
      if (inflight) clearTimeout(inflight.timer);
      inflight = null;
    },
  };
}

/** 浏览器里用 Worker，其他环境用同步兜底。 */
export function createDefaultChannel(): EquityChannel {
  if (typeof Worker !== 'undefined' && typeof window !== 'undefined') return createWorkerChannel();
  // 动态 require 在 ESM 下不可用，这里由调用方（单测）显式注入同步通道
  throw new Error('当前环境不支持 Worker，请显式注入同步通道');
}
